
import Spinner from '../Spinner.vue'
import SpreadsheetGrid from './SpreadsheetGrid.vue'
import { loadSpreadsheetEntries } from './load'
import { buildColumns } from './columns'
import { dirtyKey, originalCellString } from './cell'
import { toTSV } from './tsv'
import { buildRowPlan, cleanFields } from './save'

export default {
	name: 'SpreadsheetView',
	components: { Spinner, SpreadsheetGrid },
	// context the orchestrator needs to load entries and (later) save them
	inject: [ 'endpoint', 'locales', 'defaultLocale', 'permissions' ],
	props: {
		filter: { type: Object, required: true },
	},
	data: () => ({
		// raw entries are frozen (see load()) so Vue 2 does not deep-convert them to reactive
		// objects - the grid can hold thousands of cells without the per-property observer cost
		entries: [] as any[],
		// unsaved rows the user added; reactive (small) so their type selector works. On save
		// these become create/update targets based on the id typed into the ID column.
		newRows: [] as any[],
		newRowSeq: 0,
		// sparse overlay of edited cells: dirtyKey(entryId, columnKey) -> new string value.
		// a plain object (Vue 2 doesn't make Map reactive); mutated via $set/$delete. Only
		// changed cells live here, so it stays small regardless of grid size.
		dirty: {} as Record<string, string>,
		// per-row save outcome: rowId -> { state: 'saving'|'saved'|'error'|'conflict', message }
		rowStatus: {} as Record<string, { state: string, message: string }>,
		saving: false,
		copied: false, // transient "Copied!" feedback on the Copy table button
		total: 0,
		limit: 100,
		skip: 0,
		loading: true,
		error: null as string | null,
	}),
	computed: {
		typeLookup() {
			return (window as any).typeLookup
		},
		// loaded entries followed by any added rows; this is what the grid renders and indexes
		rows(): any[] {
			return this.entries.concat(this.newRows)
		},
		// columns are the superset of fields across the types present in loaded AND new rows, so
		// picking a type for a new row surfaces that type's field columns even on an empty result
		columns(): any[] {
			return buildColumns(this.rows, this.typeLookup, this.locales, this.defaultLocale).columns
		},
		changedCount(): number {
			return Object.keys(this.dirty).length
		},
		canSave(): boolean {
			return !this.saving && (this.changedCount > 0 || this.newRows.length > 0)
		},
	},
	watch: {
		filter: {
			deep: true,
			immediate: true,
			handler() { this.load() },
		},
		// keep the header's Save button (in ContentSpreadsheet) in sync with our state
		canSave: {
			immediate: true,
			handler(v) { this.$emit('can-save-change', v) },
		},
	},
	methods: {
		// a committed cell edit: store the new string if it differs from the loaded value,
		// otherwise drop it from the overlay so editing back to the original un-marks the cell
		onCellCommit({ entryId, columnKey, value, original }: { entryId: string, columnKey: string, value: string, original: string }) {
			const key = dirtyKey(entryId, columnKey)
			if (value === original) this.$delete(this.dirty, key)
			else this.$set(this.dirty, key, value)
		},
		addRow() {
			this.newRowSeq++
			this.newRows.push({
				sys: {
					id: 'new-' + this.newRowSeq, // local placeholder id (hidden; ID cell shows blank)
					_new: true,
					version: 0,
					contentType: { sys: { id: null } },
				},
				fields: {},
			})
		},
		// type chosen in a new row's Type selector -> sets the row's content type, which makes
		// that type's field columns applicable (editable) for the row
		onRowTypeChange({ entryId, contentTypeId }: { entryId: string, contentTypeId: string }) {
			const row = this.newRows.find(r => r.sys.id === entryId)
			if (row) row.sys.contentType.sys.id = contentTypeId || null
		},
		// a cell's current string: the edited (dirty) value if any, else the loaded value
		cellString(row: any, column: any): string {
			const override = this.dirty[dirtyKey(row.sys.id, column.key)]
			return override !== undefined ? override : originalCellString(this.typeLookup, row, column)
		},
		// copy the whole table to the clipboard as TSV, with a header row of column keys
		// (sys.id / sys.contentType / fields.<id>.<locale>) so a new sheet can be set up in Excel
		async copyTable() {
			const cols = this.columns
			const matrix: string[][] = [ cols.map(c => c.key) ]
			for (const row of this.rows) matrix.push(cols.map(c => this.cellString(row, c)))
			await this.writeClipboard(toTSV(matrix))
			this.copied = true
			setTimeout(() => { this.copied = false }, 1500)
		},
		async writeClipboard(text: string) {
			try {
				if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return }
			}
			catch { /* fall through to the execCommand fallback */ }
			const ta = document.createElement('textarea')
			ta.value = text
			ta.style.position = 'fixed'
			ta.style.opacity = '0'
			document.body.appendChild(ta)
			ta.select()
			try { document.execCommand('copy') }
			finally { document.body.removeChild(ta) }
		},
		setStatus(rowId: string, state: string, message: string) {
			this.$set(this.rowStatus, rowId, { state, message })
		},
		// drop every dirty cell belonging to a row (so its cornsilk "changed" marking clears once
		// the row is saved). The prefix is derived from dirtyKey itself so it always matches the
		// real separator; reassigning a fresh object (vs per-key $delete) reliably re-renders.
		clearRowDirty(rowId: string) {
			const prefix = dirtyKey(rowId, '') // rowId + separator
			const next: Record<string, string> = {}
			for (const key of Object.keys(this.dirty))
				if (!key.startsWith(prefix)) next[key] = this.dirty[key]
			this.dirty = next
		},
		// find an existing entry by id via the list endpoint (no 404 to handle), or null
		async fetchEntryById(id: string): Promise<any | null> {
			const list = await this.$httpGet(this.endpoint + '/entries', { 'sys.id': id })
			return list?.items?.[0] ?? null
		},
		// perform the HTTP for one row plan; returns the resulting entry (server response)
		async executePlan(plan: any): Promise<any> {
			const cleaned = cleanFields(plan.fields)
			if (plan.op === 'update') {
				// update the row's own entry in place: send the full loaded entry with new fields
				const loaded = this.entries.find(e => e.sys.id === plan.rowId)
				const entry = JSON.parse(JSON.stringify(loaded))
				entry.fields = cleaned
				return await this.$httpPut(this.endpoint + '/entries/' + plan.targetId, entry, {
					headers: { 'X-Contentful-Version': entry.sys.version },
				})
			}
			if (plan.op === 'create') {
				return await this.$httpPost(this.endpoint + '/entries', { fields: cleaned }, {
					headers: { 'X-Contentful-Content-Type': plan.contentTypeId },
				})
			}
			// upsert: write the row's data to a different id - update if it exists, else create it
			const existing = await this.fetchEntryById(plan.targetId)
			if (existing) {
				const entry = JSON.parse(JSON.stringify(existing))
				entry.fields = cleaned
				return await this.$httpPut(this.endpoint + '/entries/' + plan.targetId, entry, {
					headers: { 'X-Contentful-Version': existing.sys.version },
				})
			}
			return await this.$httpPut(this.endpoint + '/entries/' + plan.targetId, { fields: cleaned }, {
				headers: { 'X-Contentful-Content-Type': plan.contentTypeId },
			})
		},
		async save() {
			if (!this.canSave) return
			this.saving = true
			const plans = this.rows
				.map(row => buildRowPlan(row, this.columns, this.dirty, this.typeLookup))
				.filter(p => p.hasWork)
			// work on copies so the reactive arrays are reassigned once at the end
			const nextEntries = this.entries.slice()
			const nextNewRows = this.newRows.slice()
			for (const plan of plans) {
				if (plan.rowError) { this.setStatus(plan.rowId, 'error', plan.rowError); continue }
				if (plan.cellErrors.length) {
					this.setStatus(plan.rowId, 'error', plan.cellErrors.map(e => `${e.label}: ${e.message}`).join('; '))
					continue
				}
				this.setStatus(plan.rowId, 'saving', '')
				try {
					const saved = await this.executePlan(plan)
					this.setStatus(plan.rowId, 'saved', '')
					this.clearRowDirty(plan.rowId)
					if (plan.isNew) {
						// the new row became a real entry: drop it from new rows, show it as loaded
						const i = nextNewRows.findIndex(r => r.sys.id === plan.rowId)
						if (i >= 0) nextNewRows.splice(i, 1)
						if (saved?.sys) nextEntries.push(Object.freeze(saved))
					}
					else if (plan.op === 'update') {
						const i = nextEntries.findIndex(e => e.sys.id === plan.rowId)
						if (i >= 0 && saved?.sys) nextEntries[i] = Object.freeze(saved)
					}
					// retarget upsert from a loaded row: original entry untouched, dirty cleared
				}
				catch (e: any) {
					const msg = e?.message ?? (typeof e === 'string' ? e : JSON.stringify(e))
					const conflict = /version mismatch/i.test(msg) || e?.status === 409 || e?.statusCode === 409
					this.setStatus(plan.rowId, conflict ? 'conflict' : 'error', conflict ? 'Changed by someone else since load' : msg)
				}
			}
			this.entries = Object.freeze(nextEntries) as any[]
			this.newRows = nextNewRows
			this.saving = false
		},
		// conflict resolution: reload the server version of a row, discarding its local edits
		async reloadRow(rowId: string) {
			try {
				const fresh = await this.fetchEntryById(rowId)
				if (!fresh) return
				const arr = this.entries.slice()
				const i = arr.findIndex(e => e.sys.id === rowId)
				if (i >= 0) arr[i] = Object.freeze(fresh)
				this.entries = Object.freeze(arr) as any[]
				this.clearRowDirty(rowId)
				this.$delete(this.rowStatus, rowId)
			}
			catch (e: any) {
				this.setStatus(rowId, 'error', e?.message ?? String(e))
			}
		},
		async load() {
			this.loading = true
			this.error = null
			this.dirty = {} // discard edits from a previous filter/load
			this.newRows = []
			this.rowStatus = {}
			try {
				const { items, total } = await loadSpreadsheetEntries(this.endpoint, this.filter, this.limit, this.skip)
				// freeze so the raw entry graph stays inert (non-reactive); reassigning the
				// array reference is what stays reactive and re-renders the grid
				for (const entry of items) Object.freeze(entry)
				Object.freeze(items)
				this.entries = items
				this.total = total
			}
			catch (e: any) {
				console.error('SpreadsheetView: could not load entries', e)
				this.error = e?.message ?? String(e)
			}
			finally {
				this.loading = false
			}
		},
	},
}
